// ────────────────────────────────────────────────────────────────
// src/pages/QuickPayGeneratePage.js
//
// Logged-in user → creates a Quick-Pay link + QR code
//
//  • Dashboard-style layout
//  • i18n (en / ja / pt) reacts to parent Outlet language
//  • “Copy to clipboard” button with tooltip
//  • Uses QRCodeCanvas from qrcode.react
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { QRCodeCanvas } from 'qrcode.react'
import { Copy, Check } from 'lucide-react'            // small  icon library used elsewhere

function QuickPayGeneratePage() {
  const navigate   = useNavigate()
  const outletCtx  = useOutletContext() || {}
  const lang       = outletCtx.lang || localStorage.getItem('lang') || 'en'

  /* ───────────── i18n ───────────── */
  const translations = {
    en: {
      pageTitle   : 'Quick-Pay QR Code',
      subtitle    : 'Ask the payer to scan this code.  They don’t need a PayMe account.',
      done        : 'Done',
      copy        : 'Copy',
      copied      : 'Copied!',
      pleaseLogin : 'Please log in first.',
      generating  : 'Generating link…',
      errorPrefix : 'Server error:',
    },
    ja: {
      pageTitle   : 'クイックペイ用QRコード',
      subtitle    : 'このコードをスキャンしてもらうだけで、相手はPayMeアカウント不要です。',
      done        : '完了',
      copy        : 'コピー',
      copied      : 'コピーしました！',
      pleaseLogin : '最初にログインしてください。',
      generating  : 'リンクを生成中…',
      errorPrefix : 'サーバーエラー:',
    },
    pt: {
      pageTitle   : 'QR Code de Quick-Pay',
      subtitle    : 'Peça para a pessoa escanear.  Não precisa ter conta PayMe.',
      done        : 'Concluir',
      copy        : 'Copiar',
      copied      : 'Copiado!',
      pleaseLogin : 'Faça login primeiro.',
      generating  : 'Gerando link…',
      errorPrefix : 'Erro no servidor:',
    },
  }
  const t = translations[lang] || translations.en

  /* ───────────── user & state ───────────── */
  const user  = JSON.parse(localStorage.getItem('user')) || {}
  const token = localStorage.getItem('token')

  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [linkURL, setLinkURL] = useState(null)
  const [copied , setCopied ] = useState(false)

  /* ───────────── fetch link ───────────── */
  useEffect(() => {
    const run = async () => {
      if (!user?.id || !token) {
        setError(t.pleaseLogin); setLoading(false); return
      }
      try {
        const { data } = await axios.post(
          'https://payme-backend-713827320947.asia-northeast1.run.app/api/quickpay/link',
          { userId:user.id, preset_amount:null },
          { headers:{ Authorization:`Bearer ${token}` } },
        )
        setLinkURL(data.url)
      } catch (e) {
        console.error('QuickPay link error:', e)
        setError(`${t.errorPrefix} ${e.response?.data?.message || 'unknown'}`)
      } finally {
        setLoading(false)
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ───────────── handlers ───────────── */
  const handleBack = () => navigate(-1)

  const handleCopy = async () => {
    if (!linkURL) return
    try {
      await navigator.clipboard.writeText(linkURL)
      setCopied(true)
      setTimeout(()=>setCopied(false), 2000)
    } catch (e) {
      console.error('Clipboard copy failed:', e)
    }
  }

  /* ───────────── render ───────────── */
  if (loading) return <p className="m-4">{t.generating}</p>

  if (error) {
    return (
      <div className="m-4 text-red-600 space-y-3">
        <p>{error}</p>
        <button onClick={handleBack} className="text-blue-600 underline">
          {t.done}
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8 space-y-6">
      <h2 className="text-2xl font-semibold text-center">{t.pageTitle}</h2>
      <p className="text-gray-600 text-center whitespace-pre-wrap">{t.subtitle}</p>

      {/* QR code */}
      <div className="flex justify-center">
        <QRCodeCanvas
          value={linkURL}
          size={220}
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin
        />
      </div>

      {/* URL + copy button */}
      <div className="relative flex items-center bg-gray-50 border rounded p-3 text-sm break-all">
        <span className="flex-1">{linkURL}</span>
        <button
          onClick={handleCopy}
          title={copied ? t.copied : t.copy}
          className="ml-2 text-gray-600 hover:text-blue-600"
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={handleBack}
          className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
        >
          {t.done}
        </button>
      </div>
    </div>
  )
}

export default QuickPayGeneratePage
// ────────────────────────────────────────────────────────────────
// END – QuickPayGeneratePage.js
// ────────────────────────────────────────────────────────────────
